import {StyleSheet, css} from 'aphrodite';
import React, {useMemo} from 'react';

import {useIsConsolidatedAddSourceEnabled} from 'analysis-tool/AnalysisToolFeatureGates';
import {getNullableStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import {checkIsAnalysisArchived, showFlowchartEditFunctionality} from 'analysis-tool/EditAnalysisHelpers';
import {CanEditReturnValue} from 'analysis-tool/EditAnalysisHelpers';
import {checkCanModifyStep} from 'analysis-tool/EditStepHelpers';
import {
  AnalysisFlowchartAction,
  AnalysisFlowchartAvailableAction,
} from 'analysis-tool/flowchart/actions/AnalysisFlowchartActionTypes';
import AnalysisFlowchart from 'analysis-tool/flowchart/AnalysisFlowchart';
import {AnalysisFlowchartMode} from 'analysis-tool/flowchart/AnalysisFlowchartContext';
import {refreshAnalysis} from 'analysis-tool/polling/refreshPipeline';
import {
  AnalysisRightSidebarItem,
  AnalysisRightSidebarItemType,
} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import useRightSidebarItem from 'analysis-tool/ui/hooks/useRightSidebarItem';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import BenchlingConfig from 'BenchlingConfig';
import {PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';

type AnalysisFlowchartViewProps = MainViewComponentProps<AnalysisMainViewItemType.FLOWCHART>;

/** @package Exported for testing */
export function useAvailableActions(
  analysis: PipelineGraphQL,
  onChangeSelectedRightSidebarItem: AnalysisFlowchartViewProps['onChangeSelectedRightSidebarItem'],
  selectedRightSidebarItem: AnalysisRightSidebarItem | null
): Array<AnalysisFlowchartAvailableAction> {
  const consolidatedAddSourceEnabled = useIsConsolidatedAddSourceEnabled(analysis);

  return useMemo(() => {
    // Always allow read-only actions, e.g. opening the step's configuration, even if editing is disabled.
    const actions: Array<AnalysisFlowchartAvailableAction> = [
      {
        action: AnalysisFlowchartAction.OPEN_STEP_CONFIGURATION,
        onAction: ({stepId}: {stepId: string}) => {
          onChangeSelectedRightSidebarItem({type: AnalysisRightSidebarItemType.EDIT_STEP, stepId});
        },
      },
    ];
    if (BenchlingConfig.ENABLE_INSIGHTS_LABS) {
      actions.push({action: AnalysisFlowchartAction.COPY_TO_CLIPBOARD});
    }

    if (!showFlowchartEditFunctionality(analysis)) {
      return actions;
    }

    actions.push(
      {
        action: AnalysisFlowchartAction.OPEN_ADD_STEP as AnalysisFlowchartAction.OPEN_ADD_STEP,
        onAction: ({stepId, stepDataName}: {stepId: string; stepDataName: string}) => {
          onChangeSelectedRightSidebarItem({
            type: AnalysisRightSidebarItemType.ADD_STEP,
            stepId,
            stepDataName,
          });
        },
      },
      {action: AnalysisFlowchartAction.CONNECT_STEPS},
      {action: AnalysisFlowchartAction.DUPLICATE_STEPS},
      {action: AnalysisFlowchartAction.DELETE_STEPS},
      {action: AnalysisFlowchartAction.DELETE_STEP_AND_CHILDREN},
      {
        action: AnalysisFlowchartAction.RERUN_STEPS,
        getDisabledMessage: (stepId: string) => {
          const step = getNullableStepFromAnalysis(analysis, stepId);
          if (step?.statusCode === PipelineStepStatusCode.HALTED) {
            return 'Halted steps cannot be rerun';
          }
          return null;
        },
      }
    );

    if (BenchlingConfig.ENABLE_INSIGHTS_LABS) {
      actions.push({
        action: AnalysisFlowchartAction.PASTE_FROM_CLIPBOARD,
        onAction: (result: {createdStepId?: string}) => {
          if (result?.createdStepId != null) {
            onChangeSelectedRightSidebarItem({
              type: AnalysisRightSidebarItemType.EDIT_STEP,
              stepId: result.createdStepId,
            });
          }
        },
      });
    }

    // Only include OPEN_ADD_SOURCE_STEP when the sidebar is NOT already open for source steps.
    const isAddSourceStepSidebarOpen =
      selectedRightSidebarItem?.type === AnalysisRightSidebarItemType.ADD_SOURCE_STEP;

    if (!isAddSourceStepSidebarOpen && !consolidatedAddSourceEnabled) {
      actions.push({
        action: AnalysisFlowchartAction.OPEN_ADD_SOURCE_STEP,
        onAction: () => {
          onChangeSelectedRightSidebarItem({
            type: AnalysisRightSidebarItemType.ADD_SOURCE_STEP,
          });
        },
      });
    }

    return actions;
  }, [analysis, onChangeSelectedRightSidebarItem, selectedRightSidebarItem, consolidatedAddSourceEnabled]);
}

/**
 * A flowchart representation of an analysis, as rendered full-screen in the analysis tool.
 */
export default function AnalysisFlowchartView({
  analysis,
  onChangeSelectedRightSidebarItem,
  clientStateId,
  selectedItem,
}: AnalysisFlowchartViewProps): JSX.Element {
  const {selectedRightSidebarItem} = useRightSidebarItem(clientStateId);

  const canModifyStep = (stepId: string): CanEditReturnValue => {
    const step = getNullableStepFromAnalysis(analysis, stepId);
    if (step == null) {
      return {canEdit: false, message: 'This step has been deleted.'};
    }
    return checkCanModifyStep(analysis, stepId, 'step', 'modifyStep');
  };

  const availableActions = useAvailableActions(
    analysis,
    onChangeSelectedRightSidebarItem,
    selectedRightSidebarItem
  );

  return (
    <div className={css(styles.wrapper)}>
      <AnalysisFlowchart
        analysis={analysis}
        mode={AnalysisFlowchartMode.INTERACTIVE}
        availableActions={availableActions}
        canModifyStep={canModifyStep}
        canModifyAnalysis={() => checkIsAnalysisArchived(analysis) || {canEdit: true, message: null}}
        refresh={() => refreshAnalysis(analysis.id)}
        highlightedStepIds={selectedItem?.highlightedStepIds}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    width: '100%',
    position: 'relative',
  },
});
